/** Tag schema → Schema tab. Wraps the custom SchemaEditor with local draft state
 *  + save; surfaces backend schema-validation errors (400 → `{schema: [...]}`). */
import { Alert, Box } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as tagSchemasApi from '@/api/tagSchemas';
import type { TagSchemaDetail } from '@/api/tagSchemas';
import { EditorSaveBar } from '@/components/EditorSaveBar/EditorSaveBar';
import { useSnackbar } from '@/components/Snackbar';
import { usePermissions } from '@/hooks/usePermissions';

import { SchemaEditor } from '../schema/SchemaEditor';
import { emptySchema } from '../schema/schemaEditorOps';
import type { TagSchema } from '../schema/vocabulary';

function schemaErrorsOf(e: unknown): string[] | null {
  if (e instanceof ConsoleApiError && e.status === 400) {
    const body = e.body as Record<string, unknown> | undefined;
    const raw = body?.schema;
    if (Array.isArray(raw)) return raw.map((x) => String(x));
  }
  return null;
}

export function TagSchemaEditorTab({ schema, onSaved }: { schema: TagSchemaDetail; onSaved: () => void }) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_tag_schemas_manage');
  const snackbar = useSnackbar();

  const baseline = useMemo<TagSchema>(() => schema.schema ?? emptySchema(), [schema.schema]);
  const [draft, setDraft] = useState<TagSchema>(baseline);

  useEffect(() => { setDraft(baseline); }, [baseline]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(baseline), [draft, baseline]);

  const patch = useMutation({
    mutationFn: () => tagSchemasApi.update(schema.id, { schema: draft }),
    onSuccess: () => {
      snackbar.show('Schema saved.', 'success');
      onSaved();
    },
    onError: (e) => {
      if (!schemaErrorsOf(e)) {
        snackbar.show(e instanceof ConsoleApiError ? e.message : 'Save failed.', 'error');
      }
    },
  });

  const schemaErrors = schemaErrorsOf(patch.error);

  return (
    <Box>
      {schemaErrors && (
        <Alert severity="error" sx={{ mb: 2 }}>
          The schema has validation errors:
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {schemaErrors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </Alert>
      )}

      <SchemaEditor value={draft} onChange={setDraft} readOnly={!canManage} />

      <Box sx={{ mt: 2 }}>
        <EditorSaveBar
          dirty={dirty} saving={patch.isPending} canManage={canManage}
          onSave={() => patch.mutate()} onDiscard={() => setDraft(baseline)}
        />
      </Box>
    </Box>
  );
}
